import type { ReactNode } from 'react';
import {
  Link as RouterLink,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

/** Map legacy Expo Router groups to Vite SPA paths. */
export function normalizeRoutePath(href: string): string {
  let path = href;
  const queryIndex = path.indexOf('?');
  const query = queryIndex >= 0 ? path.slice(queryIndex) : '';
  if (queryIndex >= 0) {
    path = path.slice(0, queryIndex);
  }
  if (path.startsWith('/(auth)/')) {
    path = path.replace('/(auth)/', '/');
  }
  if (path.startsWith('/(app)/')) {
    path = path.replace('/(app)/', '/');
  }
  if (path === '/(app)' || path === '/(auth)') {
    return '/' + query;
  }
  return path + query;
}

/** Split href into pathname + search for React Router navigate(). */
export function parseRouteHref(href: string): { pathname: string; search: string } {
  const normalized = normalizeRoutePath(href);
  const queryIndex = normalized.indexOf('?');
  if (queryIndex < 0) {
    return { pathname: normalized, search: '' };
  }
  return {
    pathname: normalized.slice(0, queryIndex),
    search: normalized.slice(queryIndex),
  };
}

export type Href = string;

export function Redirect({ href }: { href: Href }) {
  const { pathname, search } = parseRouteHref(href);
  return <Navigate to={{ pathname, search }} replace />;
}

export function Slot() {
  return <Outlet />;
}

export function Stack({
  children,
}: {
  children?: ReactNode;
  screenOptions?: Record<string, unknown>;
}) {
  return <>{children}</>;
}

Stack.Screen = function StackScreen() {
  return null;
};

export function Link({
  href,
  children,
  onPress,
  ...props
}: {
  href: Href;
  children: ReactNode;
  style?: React.CSSProperties;
  className?: string;
  onPress?: (event: { preventDefault: () => void }) => void;
  target?: string;
}) {
  const { pathname, search } = parseRouteHref(href);
  return (
    <RouterLink
      to={{ pathname, search }}
      onClick={(e) => {
        if (onPress) {
          onPress({ preventDefault: () => e.preventDefault() });
        }
      }}
      {...props}
    >
      {children}
    </RouterLink>
  );
}

export function useRouter() {
  const navigate = useNavigate();
  return {
    push: (href: Href) => {
      const { pathname, search } = parseRouteHref(href);
      navigate({ pathname, search });
    },
    replace: (href: Href) => {
      const { pathname, search } = parseRouteHref(href);
      navigate({ pathname, search }, { replace: true });
    },
  };
}

export const router = {
  push: (href: Href) => {
    if (typeof window !== 'undefined') {
      const { pathname, search } = parseRouteHref(href);
      window.location.assign(`${pathname}${search}`);
    }
  },
  replace: (href: Href) => {
    if (typeof window !== 'undefined') {
      const { pathname, search } = parseRouteHref(href);
      window.location.replace(`${pathname}${search}`);
    }
  },
};

export function usePathname(): string {
  return useLocation().pathname;
}

export function useLocalSearchParams<T extends Record<string, string | string[] | undefined>>(): T {
  const params = useParams();
  const [search] = useSearchParams();
  const merged: Record<string, string | string[] | undefined> = { ...params };
  search.forEach((value, key) => {
    merged[key] = value;
  });
  return merged as T;
}

export function ErrorBoundary({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
